import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
} from '@mui/icons-material';
import { Change, ChangeDiff } from '../../types';
import { ChangeTrackingService } from '../../services/changeTrackingService';

/**
 * DiffViewer - Display side-by-side before/after comparison of changes
 *
 * Shows field-level diff with visual indicators for added, removed, and modified fields
 */

interface DiffViewerProps {
  change: Change;
  open: boolean;
  onClose: () => void;
  onApprove?: (changeId: string, notes?: string) => void;
  onReject?: (changeId: string, reason: string) => void;
  showActions?: boolean;
}

export const DiffViewer: React.FC<DiffViewerProps> = ({
  change,
  open,
  onClose,
  onApprove,
  onReject,
  showActions = true,
}) => {
  // ===== Computed Values =====

  const diffs = useMemo(() => {
    return ChangeTrackingService.calculateDiff(change.beforeState, change.afterState);
  }, [change]);

  // ===== Helper Functions =====

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) {
      return '(empty)';
    }

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    if (Array.isArray(value)) {
      return value.join(', ') || '(empty array)';
    }

    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }

    return String(value);
  };

  const getCellStyle = (changeType: 'added' | 'removed' | 'modified', isOldValue: boolean) => {
    if (changeType === 'added' && !isOldValue) {
      return {
        bgcolor: 'success.light',
        color: 'success.contrastText',
      };
    }

    if (changeType === 'removed' && isOldValue) {
      return {
        bgcolor: 'error.light',
        color: 'error.contrastText',
      };
    }

    if (changeType === 'modified') {
      return {
        bgcolor: isOldValue ? 'warning.light' : 'info.light',
        color: isOldValue ? 'warning.contrastText' : 'info.contrastText',
      };
    }

    return {};
  };

  const getChangeTypeChip = (changeType: 'added' | 'removed' | 'modified') => {
    const config = {
      added: { label: 'Added', color: 'success' as const },
      removed: { label: 'Removed', color: 'error' as const },
      modified: { label: 'Modified', color: 'warning' as const },
    };

    const { label, color } = config[changeType];
    return <Chip label={label} color={color} size="small" />;
  };

  // ===== Event Handlers =====

  const handleApprove = () => {
    if (onApprove) {
      onApprove(change.id);
    }
    onClose();
  };

  const handleReject = () => {
    if (onReject) {
      const reason = prompt('Please provide a reason for rejection:');
      if (reason) {
        onReject(change.id, reason);
        onClose();
      }
    }
  };

  // ===== Render =====

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' },
      }}
    >
      {/* Dialog Header */}
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            Change Details: {change.changeType} {change.entityType}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Dialog Content */}
      <DialogContent dividers>
        {/* Change Metadata */}
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" gutterBottom>
            Change Information
          </Typography>
          <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Made By:
              </Typography>
              <Typography variant="body1">{change.userName}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Date:
              </Typography>
              <Typography variant="body1">
                {new Date(change.createdAt).toLocaleString()}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Status:
              </Typography>
              <Typography variant="body1">{change.status}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Entity ID:
              </Typography>
              <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.9em' }}>
                {change.entityId || '(new)'}
              </Typography>
            </Box>
          </Box>

          {change.reason && (
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary">
                Reason:
              </Typography>
              <Typography variant="body1">{change.reason}</Typography>
            </Box>
          )}
        </Paper>

        {/* Diff Table */}
        {diffs.length > 0 ? (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width="25%">
                    <strong>Field</strong>
                  </TableCell>
                  <TableCell width="35%">
                    <strong>Before</strong>
                  </TableCell>
                  <TableCell width="35%">
                    <strong>After</strong>
                  </TableCell>
                  <TableCell width="5%" align="center">
                    <strong>Change</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {diffs.map((diff: ChangeDiff, index: number) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant="body2">
                        <strong>{diff.fieldLabel}</strong>
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {diff.field}
                      </Typography>
                    </TableCell>
                    <TableCell sx={getCellStyle(diff.changeType, true)}>
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {formatValue(diff.oldValue)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={getCellStyle(diff.changeType, false)}>
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {formatValue(diff.newValue)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {getChangeTypeChip(diff.changeType)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No field changes detected
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This may be a create or delete operation
            </Typography>
          </Paper>
        )}

        {/* Complete State Views for Create/Delete */}
        {change.changeType === 'create' && change.afterState && (
          <Paper sx={{ p: 2, mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              New Entity Data
            </Typography>
            <Box
              component="pre"
              sx={{
                bgcolor: 'grey.100',
                p: 2,
                borderRadius: 1,
                overflow: 'auto',
                maxHeight: '300px',
              }}
            >
              <code>{JSON.stringify(change.afterState, null, 2)}</code>
            </Box>
          </Paper>
        )}

        {change.changeType === 'delete' && change.beforeState && (
          <Paper sx={{ p: 2, mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Deleted Entity Data
            </Typography>
            <Box
              component="pre"
              sx={{
                bgcolor: 'grey.100',
                p: 2,
                borderRadius: 1,
                overflow: 'auto',
                maxHeight: '300px',
              }}
            >
              <code>{JSON.stringify(change.beforeState, null, 2)}</code>
            </Box>
          </Paper>
        )}
      </DialogContent>

      {/* Dialog Actions */}
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {showActions && change.status === 'pending' && (
          <>
            <Button
              variant="outlined"
              color="error"
              startIcon={<RejectIcon />}
              onClick={handleReject}
            >
              Reject
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<ApproveIcon />}
              onClick={handleApprove}
            >
              Approve
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
